import { FunctionComponent, useCallback, useMemo, useState } from "react";

import { useSearchParams } from "react-router-dom";

import { CompanyShowBucket } from "@signalist/shared-types";

import { Spinner } from "@/components/Spinner/Spinner";
import { HideOnErrorImg } from "@/components/HideOnErrorImg/HideOnErrorImg";

import {
	CompanyRowInfo,
	useGetListCompaniesQuery,
} from "@/pages/Private/redux/list/list-companies.api";
import {
	BulkFindPersonSelection,
	useBulkFindPersonMutation,
} from "@/pages/Private/redux/list/list-find-person.api";

import { FindPeopleDialog } from "../FindPeopleDialog/FindPeopleDialog";

import { CompanyPeopleCell } from "./CompanyPeopleCell";
import { CompanyShowFilter } from "./CompanyShowFilter";
import { COMPANY_SCORE_TONE_CLASS, companyScoreBadge } from "./companyScore";

interface CompaniesTabProps {
	/** The list's id, from the route. */
	listId?: string;
}

const PAGE_LIMIT = 15;

const formatEmployees = (row: CompanyRowInfo): string => {
	const { employeesFrom, employeesTo } = row.organization;

	if (employeesFrom && employeesTo) {
		return employeesFrom === employeesTo ? `${employeesTo}` : `${employeesFrom}–${employeesTo}`;
	}

	return employeesTo ? `${employeesTo}` : employeesFrom ? `${employeesFrom}` : "—";
};

const formatAddedAt = (value: Date): string =>
	new Date(value).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

/**
 * The Companies tab of a company-only list (PREC-4400), built like the tracker's Tracked Profile
 * tab: one row per company (resolved, qualified and scored, nobody searched yet) with its score,
 * segment, size and country, and a People column saying whether anyone has been searched. The
 * selection's bulk "Find person" opens PREC-4398's dialog, prefilled with the list's profiles, and
 * drives the picked companies SEARCHING → People found / No person found. Lead tabs are unaffected.
 */
export const CompaniesTab: FunctionComponent<CompaniesTabProps> = ({ listId }) => {
	const numericListId = Number(listId);
	const [, setSearchParams] = useSearchParams();
	const [page, setPage] = useState(1);
	const [companyShow, setCompanyShow] = useState<CompanyShowBucket | undefined>();
	const [search, setSearch] = useState("");
	const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
	const [opening, setOpening] = useState<{ id: number; selection: BulkFindPersonSelection } | null>(
		null
	);

	const [bulkFindPerson] = useBulkFindPersonMutation();

	const { data, isFetching, isLoading, refetch } = useGetListCompaniesQuery(
		{
			listId: numericListId,
			companyShow,
			searchValue: search.trim() || undefined,
			page,
			limit: PAGE_LIMIT,
		},
		{ skip: !numericListId, refetchOnMountOrArgChange: true }
	);

	const rows = useMemo(() => data?.rows ?? [], [data?.rows]);
	const allSelectedOnPage = rows.length > 0 && rows.every((row) => selectedIds.has(row.id));

	const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

	const toggleRow = useCallback((id: number) => {
		setSelectedIds((previous) => {
			const next = new Set(previous);

			if (next.has(id)) {
				next.delete(id);
			} else {
				next.add(id);
			}

			return next;
		});
	}, []);

	const toggleSelectAll = useCallback(() => {
		setSelectedIds((previous) => {
			if (rows.every((row) => previous.has(row.id)) && rows.length > 0) {
				return new Set();
			}

			return new Set(rows.map((row) => row.id));
		});
	}, [rows]);

	const openDialog = useCallback(
		(ids: number[]) => {
			if (ids.length === 0) {
				return;
			}

			setOpening({ id: Date.now(), selection: { listId: numericListId, ids } });
		},
		[numericListId]
	);

	const closeDialog = useCallback(() => {
		setOpening(null);
		clearSelection();
		void refetch();
	}, [clearSelection, refetch]);

	const viewPeople = useCallback(() => {
		setSearchParams((previous) => {
			const next = new URLSearchParams(previous);

			next.set("tab", "UNASSIGNED");

			return next;
		});
	}, [setSearchParams]);

	const changeShow = useCallback((bucket?: CompanyShowBucket) => {
		setCompanyShow(bucket);
		setPage(1);
	}, []);

	if (isLoading) {
		return (
			<div className="flex h-40 items-center justify-center">
				<Spinner />
			</div>
		);
	}

	const selectedCount = selectedIds.size;

	return (
		<div className="w-full">
			<div className="flex items-center gap-3 py-2">
				<input
					className="input-styles h-9 w-64"
					placeholder="Search companies"
					type="text"
					value={search}
					onChange={(event) => {
						setSearch(event.target.value);
						setPage(1);
					}}
				/>
				<CompanyShowFilter buckets={data?.buckets} value={companyShow} onChange={changeShow} />
				{isFetching ? <Spinner /> : null}
			</div>

			<div className="overflow-x-auto rounded border border-gray-200">
				<table className="w-full text-sm">
					<thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
						<tr>
							<th className="w-10 px-3 py-2">
								<input
									aria-label="Select all companies"
									checked={allSelectedOnPage}
									type="checkbox"
									onChange={toggleSelectAll}
								/>
							</th>
							<th className="px-3 py-2">Company</th>
							<th className="px-3 py-2">People</th>
							<th className="px-3 py-2">Company Segment</th>
							<th className="px-3 py-2">Number of Employees</th>
							<th className="px-3 py-2">Company HQ Country</th>
							<th className="px-3 py-2">Added At</th>
						</tr>
					</thead>
					<tbody>
						{rows.map((row) => {
							const badge = companyScoreBadge(row.score);

							return (
								<tr key={row.id} className="border-t border-gray-100 hover:bg-gray-50">
									<td className="px-3 py-2">
										<input
											aria-label={`Select ${row.organization.name ?? "company"}`}
											checked={selectedIds.has(row.id)}
											type="checkbox"
											onChange={() => toggleRow(row.id)}
										/>
									</td>
									<td className="px-3 py-2">
										<div className="flex items-center gap-2">
											{badge ? (
												<span
													className={`rounded px-1.5 py-0.5 text-xs font-semibold ${COMPANY_SCORE_TONE_CLASS[badge.tone]}`}
												>
													{badge.label}
												</span>
											) : null}
											<HideOnErrorImg
												alt="logo"
												className="h-6 w-6 rounded"
												referrerPolicy="no-referrer"
												src={row.organization.logoUrl}
											/>
											<span className="font-medium text-gray-900">
												{row.organization.name ?? "—"}
											</span>
										</div>
									</td>
									<td className="px-3 py-2">
										<CompanyPeopleCell
											row={row}
											onFindPerson={(target) => openDialog([target.id])}
											onViewPeople={viewPeople}
										/>
									</td>
									<td className="px-3 py-2 text-gray-700">
										{row.score?.category ?? row.organization.industry ?? "—"}
									</td>
									<td className="px-3 py-2 text-gray-700">{formatEmployees(row)}</td>
									<td className="px-3 py-2 text-gray-700">{row.organization.country ?? "—"}</td>
									<td className="px-3 py-2 text-gray-700">{formatAddedAt(row.addedAt)}</td>
								</tr>
							);
						})}
						{rows.length === 0 ? (
							<tr>
								<td className="px-3 py-10 text-center text-gray-400" colSpan={7}>
									No companies to show.
								</td>
							</tr>
						) : null}
					</tbody>
				</table>
			</div>

			<div className="flex items-center justify-between py-3 text-sm text-gray-500">
				<span>
					{data?.count ?? 0} {data?.count === 1 ? "company" : "companies"}
				</span>
				<div className="flex items-center gap-2">
					<button
						className="rounded border border-gray-200 px-2 py-1 disabled:opacity-40"
						disabled={page <= 1}
						type="button"
						onClick={() => setPage((current) => Math.max(1, current - 1))}
					>
						Previous
					</button>
					<span>Page {page}</span>
					<button
						className="rounded border border-gray-200 px-2 py-1 disabled:opacity-40"
						disabled={(data?.count ?? 0) <= page * PAGE_LIMIT}
						type="button"
						onClick={() => setPage((current) => current + 1)}
					>
						Next
					</button>
				</div>
			</div>

			{selectedCount > 0 ? (
				<div className="fixed inset-x-0 bottom-4 z-30 mx-auto flex w-fit items-center gap-4 rounded-full border border-gray-200 bg-white px-5 py-2 shadow-lg">
					<span className="text-sm font-medium">{selectedCount} selected</span>
					<button
						className="rounded bg-blue-action px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-action-dark"
						data-testid="companies-bulk-find-person"
						type="button"
						onClick={() => openDialog([...selectedIds])}
					>
						Find person
					</button>
					<button
						className="text-sm text-gray-500 hover:underline"
						type="button"
						onClick={clearSelection}
					>
						Clear
					</button>
				</div>
			) : null}

			{opening ? (
				<FindPeopleDialog
					listId={numericListId}
					selection={opening.selection}
					sessionKey={`${numericListId}-companies-${opening.id}`}
					onClose={closeDialog}
					onSubmit={(request) => bulkFindPerson(request).unwrap()}
				/>
			) : null}
		</div>
	);
};
